/**
 * Detects which in-app / web payment provider is available.
 * Priority: CBE Birr Plus → CBE SuperApp → M-Pesa → Telebirr → Web C2B
 */

import { isCbeBirrPlusAvailable } from "@/lib/cbe-birr-plus"
import { getCbeWindow } from "@/types/cbe-superapp"

export type PaymentProvider = "cbe_birr" | "cbe" | "mpesa" | "telebirr" | "c2b"

export const MPESA_ORIGIN = "https://mpesa-test.qetero.com"
export const QETERO_ORIGINS = ["https://qetero.com", "http://qetero.com"] as const

/** Hardcoded Cheche payment method IDs used when the host auto-selects */
export const PAYMENT_METHOD_IDS = {
  mpesa: "ceb47a9e-5138-4dda-a0df-525338c57472",
  telebirr: "c3d73cc3-e0c1-4af0-9165-d726f21a0c18",
  cbeBirrUssd: "c714ec83-fc7d-4029-9448-08ca2e3570f4",
  qeteroC2b: "c3d73cc3-e0c1-4af0-9165-d726f21a0c18",
} as const

/** Methods shown in the browser C2B picker */
export const BROWSER_C2B_PAYMENT_METHOD_IDS = [
  PAYMENT_METHOD_IDS.telebirr,
  PAYMENT_METHOD_IDS.cbeBirrUssd,
] as const

export function isBrowserC2bPaymentMethod(method: { id?: string; name?: string }): boolean {
  const id = method.id || ""
  if ((BROWSER_C2B_PAYMENT_METHOD_IDS as readonly string[]).includes(id)) return true
  const name = (method.name || "").toLowerCase()
  return name.includes("telebirr") || name.includes("cbe birr ussd")
}

export function isCbeSuperAppAvailable(): boolean {
  if (typeof window === "undefined") return false
  return typeof getCbeWindow().cbesuperapp?.send === "function"
}

export function isTelebirrSuperAppAvailable(): boolean {
  if (typeof window === "undefined") return false
  return window.consumerapp != null
}

export function isMpesaOrigin(origin = typeof window !== "undefined" ? window.location.origin : ""): boolean {
  return origin === MPESA_ORIGIN
}

export function isQeteroOrigin(origin = typeof window !== "undefined" ? window.location.origin : ""): boolean {
  return (QETERO_ORIGINS as readonly string[]).includes(origin)
}

/**
 * Resolve the active payment provider for the current runtime.
 * In-app bridges take priority over origin-based defaults.
 */
let lastDetectedProvider: PaymentProvider | null = null

export function detectPaymentProvider(
  origin = typeof window !== "undefined" ? window.location.origin : ""
): PaymentProvider {
  const birr = isCbeBirrPlusAvailable()
  const superApp = isCbeSuperAppAvailable()
  const mpesa = isMpesaOrigin(origin)
  const telebirr = isTelebirrSuperAppAvailable()
  const provider: PaymentProvider = birr ? "cbe_birr" : superApp ? "cbe" : mpesa ? "mpesa" : telebirr ? "telebirr" : "c2b"
  if (lastDetectedProvider !== provider) {
    lastDetectedProvider = provider
    console.log("[CBE Birr Plus] detectPaymentProvider", {
      origin,
      birr,
      superApp,
      mpesa,
      telebirr,
      provider,
    })
  }
  return provider
}

/** True when the UI should hide the payment method picker (auto-selected). */
export function shouldAutoSelectPaymentMethod(provider: PaymentProvider, origin?: string): boolean {
  if (provider === "cbe_birr" || provider === "cbe" || provider === "mpesa" || provider === "telebirr") return true
  return false
}

/** i18n key for where the app is currently opened (shown in confirm modal). */
export function getOpenedInLabelKey(provider: PaymentProvider = detectPaymentProvider()): string {
  switch (provider) {
    case "cbe_birr":
      return "openedInCbeBirrPlus"
    case "cbe":
      return "openedInCbeApp"
    case "mpesa":
      return "openedInMpesaApp"
    case "telebirr":
      return "openedInTelebirrApp"
    default:
      return "openedInBrowser"
  }
}
